import FormProvider, { RHFSelect, RHFTextField } from '@/commons/hook-form';
import RHFSwitchV2 from '@/commons/hook-form/RHFSwitchV2';
import Label from '@/commons/label';
import { getRoles } from '@/services';
import { Roles, rolsByOtherCompany, rolsByRestaurant, translates } from '@/utils';
import { Button } from '@PorkyCol/button';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthContext } from '@hooks/useAuthContext';
import { Box, Card, FormControlLabel, Grid, Stack, Switch, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useCompanyCategory } from '@/hooks/useCompanyCategory';
import { Controller, useForm } from 'react-hook-form';
import { IUserAccountGeneral } from '../../types';
import { defaultValuesUserForm } from './initial-values';
import { editUserSchema, newUserSchema } from './validation';

type FormValuesProps = Omit<IUserAccountGeneral, 'id' | 'uid'>;

type Props = {
  isEdit?: boolean;
  current?: IUserAccountGeneral;
  isLoading?: boolean;
  onSave: (values: any) => Promise<any>;
};

export default function UserNewEditForm({ isEdit = false, current, onSave, isLoading }: Props) {
  const { user } = useAuthContext();
  const { data: dataRols } = useQuery({
    queryKey: ['rols'],
    queryFn: getRoles,
  });
  const { isFoodCompany } = useCompanyCategory();

  const defaultValues = useMemo(
    () => defaultValuesUserForm({ ...current, branch: user?.branchSelected }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [current]
  );

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(isEdit ? editUserSchema : newUserSchema),
    defaultValues,
  });

  const { reset, watch, control, handleSubmit } = methods;

  const values = watch();

  useEffect(() => {
    if (isEdit && current) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, current]);

  const onSubmit = async (data: FormValuesProps) => {
    try {
      await onSave({ ...data, password: data.password.length ? data.password : undefined });
      reset();
    } catch (error) {
      console.error(error);
    }
  };

  const filterRols = isFoodCompany ? rolsByRestaurant : rolsByOtherCompany;

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card sx={{ pt: 2, pb: 2, px: 3 }}>
            <Typography variant="h6" textAlign="center" sx={{ mb: 3 }}>
              Atributos
            </Typography>
            {isEdit && (
              <>
                <Label
                  color={values.status ? 'success' : 'error'}
                  sx={{ textTransffieldorm: 'uppercase', position: 'absolute', top: 24, right: 24 }}
                >
                  {values.status ? 'active' : 'banned'}
                </Label>
                <FormControlLabel
                  labelPlacement="start"
                  control={
                    <Controller
                      name="status"
                      control={control}
                      render={({ field }) => (
                        <Switch
                          {...field}
                          checked={field.value}
                          onChange={(event) => field.onChange(event.target.checked)}
                        />
                      )}
                    />
                  }
                  label={
                    <>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Estado
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Apply disable account
                      </Typography>
                    </>
                  }
                  sx={{ mx: 0, mb: 1, width: 1, justifyContent: 'space-between' }}
                />
              </>
            )}
            <RHFSwitchV2 label={null} name="isShift" title="Es turnero" />
            <RHFSwitchV2 label={null} name="isTips" title="Recibe propina" />
            <RHFSwitchV2 name="branch" label={null} title={translates.isBranch} checked />
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Card sx={{ p: 3, gap: 2 }}>
            <Box
              rowGap={3}
              columnGap={2}
              display="grid"
              gridTemplateColumns={{
                xs: 'repeat(1, 1fr)',
                sm: 'repeat(2, 1fr)',
              }}
            >
              <RHFTextField name="name" label={translates.name} />
              <RHFTextField name="email" label={translates.email} />
              <Stack sx={{ display: 'flex', flexDirection: 'row', gap: '1rem' }}>
                <RHFTextField
                  name="indicatorPhone"
                  label={translates.indicativeNumber}
                  sx={{ width: '5rem' }}
                />
                <RHFTextField name="phone" label={translates.phone} />
              </Stack>
              <RHFTextField name="password" label={translates.password} type="password" />
              <RHFSelect native name="rol" label={translates.rol} fullWidth>
                <option value="" selected disabled hidden />
                {dataRols
                  ?.filter((rol: any) => filterRols.includes(rol.id))
                  .map?.((role: any) => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
              </RHFSelect>
              {values.isShift ? (
                <RHFTextField name="priceHours" label={translates.priceHours} />
              ) : null}
            </Box>
            <Box mt={3} />

            <Stack alignItems="flex-end" sx={{ mt: 3 }}>
              <Button type="submit" variant="solid" loading={isLoading} color="success">
                {!isEdit ? [translates.create + translates.user] : [translates.saveChanges]}
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
